<template>
    <FormBox header="Angaben zu beteiligten Personen">
        <div v-if="verfasserError" class="form-error mb-2">
            <div>{{ verfasserError }}</div>
        </div>
        <div v-if="duplicateRoleErrors.length" class="form-error mb-2">
            <div v-for="msg in duplicateRoleErrors" :key="msg">{{ msg }}</div>
        </div>

        <RepeatableField
            :model-value="form.persons"
            @update:model-value="form.persons = $event"
            :max-items="20"
            :create-empty="() => ({ person: null, authorType: null, _createNew: false, _firstname: '', _lastname: '' })"
        >
            <template #default="{ index, isFirst }">
                <div class="row">
                    <div class="col-12 col-lg-9">
                        <!-- Create new person: firstname + lastname fields -->
                        <template v-if="form.persons[index]._createNew">
                            <div class="form-highlight" :class="{ 'mb-3': false }">
                                <label class="form-label-alt">
                                    <span>Neue Person</span>
                                </label>
                                <div class="d-flex gap-2 align-items-start">
                                    <input
                                        v-model="form.persons[index]._firstname"
                                        type="text"
                                        class="form-control"
                                        placeholder="Vorname"
                                    >
                                    <input
                                        v-model="form.persons[index]._lastname"
                                        type="text"
                                        class="form-control"
                                        placeholder="Nachname"
                                        :required="isFirst"
                                    >
                                    <button
                                        type="button"
                                        class="lfmpub-select__cancel"
                                        @click="cancelCreate(index)"
                                        title="Abbrechen"
                                    ><i class="fa-thin fa-arrow-rotate-left"></i></button>
                                </div>
                            </div>
                        </template>

                        <!-- Normal select with "create new" option -->
                        <template v-else>
                            <FormSelect
                                :model-value="form.persons[index].person"
                                @update:model-value="form.persons[index].person = $event"
                                :options="payload.persons"
                                option-value="uid"
                                option-label="fullName"
                                label="Person"
                                :tooltip="isFirst ? 'persons.person' : ''"
                                placeholder="Person auswählen"
                                :required="isFirst"
                                :no-margin="true"
                            >
                                <template #before-options>
                                    <div
                                        class="lfmpub-select__option lfmpub-select__option--create-entity"
                                        @click="startCreate(index)"
                                    >
                                        <i class="fa-thin fa-plus me-1"></i> Neue Person anlegen
                                    </div>
                                </template>
                            </FormSelect>
                        </template>
                    </div>
                    <div class="col-12 col-lg-3">
                        <FormSelect
                            :model-value="form.persons[index].authorType"
                            @update:model-value="form.persons[index].authorType = $event"
                            :options="payload.authorTypes"
                            :disabled-values="takenRestrictedTypesExcept(index)"
                            option-value="uid"
                            option-label="type"
                            label="Funktion"
                            :tooltip="isFirst ? 'persons.authorType' : ''"
                            placeholder="Funktion"
                            :required="isFirst"
                            :no-margin="true"
                        />
                    </div>
                </div>
            </template>
        </RepeatableField>
    </FormBox>
</template>

<script setup>
import {computed, inject} from 'vue';
import FormBox from './FormBox.vue';
import FormSelect from './FormSelect.vue';
import RepeatableField from './RepeatableField.vue';

const props = defineProps({
    form: { type: Object, required: true },
    payload: { type: Object, required: true },
});

const submitted = inject('submitted', {value: false});

// payload.authorTypes is serialised from AuthorTypeRole + #[Cardinality].
// Each entry: { uid, type, typeShort, minCount, maxCount }.

function countByAuthorType() {
    const counts = {};
    props.form.persons.forEach(row => {
        if (row.person == null && !row._createNew) return;
        const t = row.authorType == null ? null : Number(row.authorType);
        if (t !== null) counts[t] = (counts[t] ?? 0) + 1;
    });
    return counts;
}

/** Role uids to grey out in row `index` (maxCount reached in other rows). */
function takenRestrictedTypesExcept(index) {
    const taken = [];
    const otherCounts = {};
    props.form.persons.forEach((row, i) => {
        if (i === index) return;
        const t = row.authorType == null ? null : Number(row.authorType);
        if (t !== null) otherCounts[t] = (otherCounts[t] ?? 0) + 1;
    });
    props.payload.authorTypes.forEach(at => {
        if (at.maxCount == null) return;
        if ((otherCounts[Number(at.uid)] ?? 0) >= at.maxCount) {
            taken.push(Number(at.uid));
        }
    });
    return taken;
}

// minCount >= 1 violations (only when at least one person is on the form;
// empty list is handled upstream).
const verfasserError = computed(() => {
    if (!submitted.value) return '';
    const validPersons = props.form.persons.filter(p => p.person != null || p._createNew);
    if (validPersons.length === 0) return '';
    const counts = countByAuthorType();
    const missing = props.payload.authorTypes.filter(at =>
        at.minCount >= 1 && (counts[Number(at.uid)] ?? 0) < at.minCount
    );
    if (missing.length === 0) return '';
    return missing
        .map(at => at.minCount === 1
            ? `Es muss mindestens ein/eine ${at.type} ausgewählt werden.`
            : `Es müssen mindestens ${at.minCount} Einträge mit Funktion "${at.type}" vorhanden sein.`)
        .join(' ');
});

// maxCount violations — mostly catches legacy duplicates; fresh forms
// can't reach this because the dropdown greys out taken roles.
const duplicateRoleErrors = computed(() => {
    if (!submitted.value) return [];
    const counts = countByAuthorType();
    const msgs = [];
    props.payload.authorTypes.forEach(at => {
        if (at.maxCount == null) return;
        const c = counts[Number(at.uid)] ?? 0;
        if (c > at.maxCount) {
            msgs.push(at.maxCount === 1
                ? `Es darf nur ein/eine ${at.type} ausgewählt werden.`
                : `Es dürfen höchstens ${at.maxCount} Einträge mit Funktion "${at.type}" vorhanden sein.`);
        }
    });
    return msgs;
});

function startCreate(index) {
    const row = props.form.persons[index];
    row._createNew = true;
    row._firstname = '';
    row._lastname = '';
    row.person = null;
}

function cancelCreate(index) {
    const row = props.form.persons[index];
    row._createNew = false;
    row._firstname = '';
    row._lastname = '';
}
</script>
